<template>
    <p class="body-small">{{ intro ?? t("components.calendar.settings.intro") }}</p>
    <label class="flex flex-col gap-2" :for="timezoneInputId">
        <span class="body-small">{{ t("components.calendar.settings.timeZone") }}</span>
        <BaseCombobox
            v-model="selectedTimezone"
            :input-id="timezoneInputId"
            :clearable="false"
            :options="calendarIntegration.timezones.map((timezone) => ({ label: timezone, value: timezone }))">
        </BaseCombobox>
    </label>
    <div class="flex flex-col gap-2">
        <div class="body-small">
            {{ t("components.calendar.settings.connection") }}
            <span class="ml-2 body-small text-ink-muted">{{ " " + t("components.calendar.settings.disconnectAnytime") }}</span>
        </div>
        <CalendarIntegrationDisconnect />
    </div>
    <div v-if="!!calendarIntegration?.private_toggle_enabled" class="flex flex-col gap-2 w-full">
        <p class="body-small text-ink-muted">{{ t("components.calendar.settings.privateEventsDefault") }}</p>

        <label class="body-small flex items-center gap-2" for="private-event">
            {{ t("components.calendar.settings.includePrivateEvents") }}
            <BaseSwitch id="private-event" v-model="allowPrivateEvents" size="sm" />
        </label>
    </div>
    <Teleport defer :to="footerTarget">
        <BaseButton
            v-if="hasCalendarProvider"
            class="flex items-center justify-center gap-2"
            theme="primary"
            :disabled="!isTimezoneSelected || isFetchingAuthUrl || awaitingSignIn"
            @click="startCalendarIntegration()">
            <LoadingSpinner v-if="isFetchingAuthUrl || awaitingSignIn" class="w-4 h-4" />
            <span>{{ awaitingSignIn ? t("components.calendar.settings.connecting") : t("components.calendar.settings.confirmAndConnect") }}</span>
        </BaseButton>
        <p v-else class="body-small text-ink-muted mt-2 text-center">
            {{ t("components.calendar.settings.unavailable") }}
        </p>
    </Teleport>
</template>

<script lang="ts">
// Module scope outlives the step, so a step remounted mid sign-in (dialog reopened, tab revisited) resumes polling.
// A sign-in nobody completes stops being polled after this window.
const SIGN_IN_WINDOW_MS = 5 * 60_000;
let popupSignInDeadline = 0;
</script>

<script setup lang="ts">
import { Browser } from "@capacitor/browser";
import { router, usePage } from "@inertiajs/vue3";
import { useTimeoutFn, useTimeoutPoll } from "@vueuse/core";
import { useFetch } from "~vue/api";
import type { UserDetails } from "~vue/appTypes";
import BaseButton from "~vue/components/BaseButton.vue";
import BaseCombobox from "~vue/components/BaseCombobox.vue";
import BaseSwitch from "~vue/components/BaseSwitch.vue";
import LoadingSpinner from "~vue/icons/LoadingSpinner.vue";
import type { CalendarIntegration } from "~vue/types/chat_home";
import { isInIFrame } from "~vue/utils";
import { logUserInteraction } from "~vue/utils/logUtils";
import { computed, inject, onMounted, onUnmounted, type Ref, ref, useId, watch } from "vue";
import { useI18n } from "vue-i18n";

import CalendarIntegrationDisconnect from "./CalendarIntegrationDisconnect.vue";

const { t } = useI18n();

const isMobileApp = usePage().props.is_mobile_app;
const props = defineProps<{
    // Names the connect surface for calendar_integration_started, which the auth-url view logs.
    source: string;
    deeplinkSource?: string;
    // Where the same-window OAuth redirect returns to; defaults to the current page.
    nextUrl?: string;
    footerTarget: string;
    intro?: string;
}>();
const emit = defineEmits<{ connected: []; error: [message: string]; "timezone-updated": [] }>();
const userDetails = inject<Ref<UserDetails>>("user_details")!;
const calendarIntegration = inject<CalendarIntegration>("calendarIntegration")!;
// The connect flow builds /calendar_integration/<provider>/auth-url/ from the org's provider.
// If the org has no provider configured, that request is null (ENG-1423), so gate the connect
// affordance on a provider being present. The CALENDAR_INTEGRATION flag can be on without a
// provider (global default / rollout), so the flag alone is not a sufficient guard.
const hasCalendarProvider = computed(() => Boolean(calendarIntegration?.calendar_provider));
const selectedTimezone = ref<{ label: string; value: string } | null>(null);
const timezoneInputId = useId();
const allowPrivateEvents = ref(calendarIntegration?.allow_private_events ?? !calendarIntegration?.private_toggle_enabled);

const authUrl = computed(() => {
    const params = new URLSearchParams();
    if (isInIFrame()) {
        params.set("teams", "true");
    }
    params.set("allow_private_events", String(allowPrivateEvents.value));
    const nextPath = props.nextUrl ?? globalThis.location.pathname;
    if (nextPath !== "/") {
        params.set("next", nextPath);
    }
    params.set("source", props.source);
    if (props.deeplinkSource) {
        params.set("deeplink_source", props.deeplinkSource);
    }
    return `/calendar_integration/${calendarIntegration?.calendar_provider}/auth-url/?${params}`;
});
const { data: authData, execute: fetchAuthUrl, isFetching: isFetchingAuthUrl, onFetchError: onFetchingAuthError } = useFetch(authUrl, { immediate: false }).get().json();

// Refer to ADR 8 for more info about MS Teams Oauth for Calendar Integration
const { execute: checkStatus, data: statusData } = useFetch("/api/calendar_integration/status/", { immediate: false }).get().json();

const checkCalendarIntegrationComplete = async () => {
    if (Date.now() > popupSignInDeadline) {
        stopPolling();
        return false;
    }
    try {
        await checkStatus();
        return statusData.value?.calendarIntegrationStatus || false;
    } catch {
        return false;
    }
};

onFetchingAuthError(() => {
    emit("error", t("components.calendar.settings.connectError"));
    logUserInteraction("calendar_integration_error", { calendar_error_type: "auth_url_fetch_error" });
});

const timezonePayload = computed(() => ({ timezone: selectedTimezone.value?.value }));
const { execute: updateTimezone } = useFetch("/api/accounts/user", { immediate: false }).put(timezonePayload).json();

const isTimezoneSelected = computed(() => selectedTimezone.value?.value != null);

const { resume: startPolling, pause: stopPolling } = useTimeoutPoll(
    checkCalendarIntegrationComplete,
    2000, // Poll every 2 seconds
    {
        immediate: false,
    },
);

watch(statusData, (data) => {
    if (data?.calendarIntegrationStatus === true) {
        popupSignInDeadline = 0;
        stopPolling();
        if (isMobileApp) {
            Browser.close();
        }
        emit("connected");
    }
});

// Only an attempt started here shows "Connecting…", and only for a minute, so a cancelled popup can be retried.
// A remounted step keeps polling silently for an earlier attempt without locking the button.
const awaitingSignIn = ref(false);
const { start: startAwaitingTimeout } = useTimeoutFn(() => (awaitingSignIn.value = false), 60_000, { immediate: false });
const beginAwaitingSignIn = () => {
    popupSignInDeadline = Date.now() + SIGN_IN_WINDOW_MS;
    awaitingSignIn.value = true;
    startAwaitingTimeout();
    startPolling();
};

const startCalendarIntegration = () => {
    updateUserTimezone();
    redirectForCalendarIntegration();
};

let isUnmounted = false;
onUnmounted(() => (isUnmounted = true));

const redirectForCalendarIntegration = async () => {
    // Never build/fetch /calendar_integration/<provider>/auth-url/ without a provider (ENG-1423).
    if (!hasCalendarProvider.value) {
        return;
    }
    await fetchAuthUrl();
    // The step can close mid-request (tab switch, dialog closed); a late redirect would discard work elsewhere.
    if (isUnmounted) {
        return;
    }

    if (authData.value?.auth_url) {
        if (isMobileApp) {
            // Mobile app: open in SFSafariViewController via Capacitor Browser plugin.
            // Poll for completion — the OAuth callback closes the browser automatically.
            Browser.open({ url: authData.value.auth_url });
            beginAwaitingSignIn();
        } else if (isInIFrame()) {
            // Use magic login URL if available for Teams, otherwise fall back to auth URL
            const popupUrl = authData.value.magic_login_url || authData.value.auth_url;
            globalThis.open(popupUrl, "_blank");
            beginAwaitingSignIn();
        } else {
            // Standard web flow - redirect in same window
            globalThis.location.href = authData.value.auth_url;
        }
    }
};

const updateUserTimezone = async () => {
    await updateTimezone();
    emit("timezone-updated");
    router.reload({ only: ["user_details"] });
};

onMounted(() => {
    if (Date.now() < popupSignInDeadline) {
        startPolling();
    }
    if (userDetails.value.timezone) {
        selectedTimezone.value = { label: userDetails.value.timezone, value: userDetails.value.timezone };
    }
});
</script>
